import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import i18next from 'i18next'
import { isNil, isUndefined, omitBy } from 'lodash'
import qs from 'query-string'

import cookie from './cookie'
import { pathSettingsBannedWords } from './paths'

export const axiosConfig: AxiosRequestConfig = {
  baseURL: window?.gon?.REACT_API_URL,
  responseType: 'json',
  timeout: 60000,
  headers: {
    Agent: `YayWeb ${window?.gon?.version}`,
    'X-Device-Info': `Yay ${window?.gon?.version} Web (${navigator.userAgent})`,
  },
  paramsSerializer: function (params = {}) {
    return qs.stringify(
      omitBy(params, (param) => isUndefined(param)),
      { arrayFormat: 'bracket' }
    )
  },
}

export type ApiClient =
  | 'default'
  | 'bare'
  | 'yayJWT'
  | 'self'
  | 'free'
  | 'youtube'
  | 'settingsYay'
  | 'cas'
  | 'idCardCheck'

export const clients: Record<ApiClient, { client: AxiosInstance }> = {
  default: {
    client: axios.create({
      ...axiosConfig,
      headers: axiosConfig.headers,
    }),
  },
  bare: {
    client: axios.create(axiosConfig),
  },
  yayJWT: {
    client: axios.create(axiosConfig),
  },
  self: {
    client: axios.create({
      ...axiosConfig,
      baseURL: '/',
      headers: {
        ...axiosConfig.headers,
        ...omitBy(
          {
            'X-CSRF-Token': document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content'),
          },
          (header) => !header
        ),
      },
    }),
  },
  free: {
    client: axios.create({
      ...axiosConfig,
      baseURL: '',
    }),
  },
  youtube: {
    client: axios.create({
      ...axiosConfig,
      baseURL: 'https://www.googleapis.com',
    }),
  },
  settingsYay: {
    client: axios.create({
      ...axiosConfig,
      baseURL: 'https://settings.yay.space',
    }),
  },
  cas: {
    client: axios.create({
      ...axiosConfig,
      baseURL: window?.gon?.CAS_API_URL,
      headers: {
        ...axiosConfig.headers,
        'X-CSRF-Token': document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content'),
      },
    }),
  },
  idCardCheck: {
    client: axios.create({
      ...axiosConfig,
      baseURL: process.env.IDCARDCHECK_API_URL,
      params: {
        'Api-Key': process.env.IDCARDCHECK_API_KEY,
      },
    }),
  },
}

const defaultRequestInterceptor =
  (auth = false) =>
  (config: AxiosRequestConfig) => {
    if (auth && cookie.accessToken.get() && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${cookie.accessToken.get()}`
    }

    config.headers['Accept-Language'] = i18next.language

    return config
  }

const defaultResponseInterceptor = (response: AxiosResponse) => {
  const token =
    response?.data?.access_token ?? response?.data?.res?.access_token

  // make access token always fresh
  if (!isNil(token)) {
    cookie.accessToken.set(token)
  }

  // make refresh token always fresh
  if (!isNil(response?.data?.refresh_token)) {
    cookie.refreshToken.set(response?.data?.refresh_token)
  }

  return response
}

const yayJWTRequestInterceptor = async (request: AxiosRequestConfig) => {
  const jwtResponse = await clients.self.client.get('/api/jwt')

  request.headers['X-Jwt'] = jwtResponse.data?.jwt
  request.headers['X-App-Version'] = window?.gon?.version

  return request
}

const settingsYayRequestInterceptor = (config: AxiosRequestConfig) => {
  if (config.url.indexOf('http') !== 0) {
    const language = (() => {
      // Judging from STG/PROD, no matter what current locale is,
      // banned words seems to stick with the Japanese ones.
      if (config?.url == pathSettingsBannedWords) {
        return 'jp'
      }

      switch (i18next.language) {
        case 'jp':
        case 'ja':
          return 'jp'
        case 'th':
          return 'th'
        case 'id':
          return 'id'
        default:
          return 'other'
      }
    })()

    config.url = `/${language}/api${config.url}`
  }

  return config
}

// request interceptor
clients.default.client.interceptors.request.use(defaultRequestInterceptor(true))
clients.self.client.interceptors.request.use(defaultRequestInterceptor(true))
clients.cas.client.interceptors.request.use(defaultRequestInterceptor(true))
clients.settingsYay.client.interceptors.request.use(
  defaultRequestInterceptor(false)
)
clients.settingsYay.client.interceptors.request.use(
  settingsYayRequestInterceptor
)
clients.yayJWT.client.interceptors.request.use(defaultRequestInterceptor(true))
clients.yayJWT.client.interceptors.request.use(yayJWTRequestInterceptor)
clients.idCardCheck.client.interceptors.request.use(
  defaultRequestInterceptor(false)
)

// response interceptor
clients.default.client.interceptors.response.use(defaultResponseInterceptor)
clients.self.client.interceptors.response.use(defaultResponseInterceptor)
clients.bare.client.interceptors.response.use(defaultResponseInterceptor)
